/**
 * Formats a price (in cents) into a money string using the shop's currency format.
 * @param {number} cents - The price in cents.
 * @param {string} format - The shop money format string (e.g. "{{amount_with_comma_separator}} €").
 * @returns {string}
 */
function formatMoney(cents, format) {
  const amount = (cents / 100).toFixed(2);
  const [int, dec] = amount.split('.');

  const withSeparator = (sep) => int.replaceAll(/\B(?=(\d{3})+(?!\d))/g, sep);

  const replacements = {
    '{{amount}}': `${withSeparator(',')}.${dec}`,
    '{{amount_no_decimals}}': withSeparator(','),
    '{{amount_with_comma_separator}}': `${withSeparator('.')},${dec}`,
    '{{amount_no_decimals_with_comma_separator}}': withSeparator('.'),
    '{{amount_with_apostrophe_separator}}': `${withSeparator("'")}.${dec}`,
  };

  return format.replaceAll(/\{\{\s*\w+\s*\}\}/g, (match) => replacements[match.replace(/\s/g, '')] ?? match);
}

const BULK_ORDER_INVENTORY_RANK = {
  out_of_stock: 0,
  backordered: 1,
  low: 2,
  in_stock: 3,
};

class BulkOrderList extends HTMLElement {
  constructor() {
    super();

    const table = this.querySelector('table.bulk-order-table');
    const rows = Array.from(table.tBodies[0].rows);
    rows.sort((a, b) => Number.parseInt(a.dataset.order, 10) - Number.parseInt(b.dataset.order, 10));
    rows.forEach((row) => table.tBodies[0].appendChild(row));

    this.tableBody = this.querySelector('.bulk-order-table tbody');
    this.submitButton = this.querySelector('button[type="submit"]');

    this.variantRows = Array.from(this.tableBody.querySelectorAll('tr.bulk-order-list__variant'));
    this.variantDataCache = new WeakMap();

    this.tableWrapper = this.querySelector('.bulk-order-table-wrapper');
    this.searchInput = table.querySelector('input.sku-input');
    this.btnClearSearch = table.querySelector('button.btn-clear-search');
    this.btnExpand = this.querySelector('button.bulk-order-expand');
    this.btnReduce = this.querySelector('button.bulk-order-reduce');
    this.btnSorts = Array.from(this.querySelectorAll('.bulk-order-sort'));
    this.colSortBtns = Array.from(this.querySelectorAll('.bulk-order-col-sort'));
    this.btnReview = table.querySelector('button.review-selected');
    this.activeSort = { key: null, direction: 1 };
    this.titleSortDirection = 1;
    this.selectedTextEl = this.querySelector('.bulk-order-selected-text');
    this.submitLabelEl = this.querySelector('.bulk-order-submit-label');
    this.variantsToggleBtn = this.querySelector('.bulk-order-variants-toggle');
    this.variantsToggleMore = this.querySelector('.bulk-order-variants-toggle__more');
    this.variantsToggleLess = this.querySelector('.bulk-order-variants-toggle__less');
    this.requireLogin = this.dataset.requireLogin === 'true';

    this.boundBeforeUnload = this.beforeUnload.bind(this);
    this.boundMql = null;
    this.boundMqlChange = this.onViewportChange.bind(this);

    this.searchInput.addEventListener('input', this.filterBySearch.bind(this));
    this.btnClearSearch.addEventListener('click', this.clearSearch.bind(this));
    this.btnExpand.addEventListener('click', this.onExpandColumns.bind(this));
    this.btnReduce.addEventListener('click', this.onReduceColumns.bind(this));
    this.btnSorts.forEach((btn) => btn.addEventListener('click', this.onTitleSort.bind(this)));
    this.colSortBtns.forEach((btn) =>
      btn.addEventListener('click', () => this.onColumnSort(btn.dataset.sortKey))
    );
    this.btnReview.addEventListener('click', this.reviewSelected.bind(this));
    this.submitButton?.addEventListener('click', this.handleSubmit.bind(this));
    this.variantsToggleBtn?.addEventListener('click', this.onVariantsToggle.bind(this));

    this.initializeVariantRows();
    this.initializeMobileRowToggles();

    this.cart = document.querySelector('cart-drawer');

    this.syncColumnModeUI();
    this.updateTotal();
  }

  onVariantsToggle() {
    const expanded = this.classList.toggle('bulk-order-list--variants-expanded');
    this.variantsToggleBtn?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (this.variantsToggleMore) this.variantsToggleMore.hidden = expanded;
    if (this.variantsToggleLess) this.variantsToggleLess.hidden = !expanded;
  }

  /** Desktop ≥750px: expanded unless `.bulk-order-list--desktop-columns-reduced`. Mobile: all rows have `.bulk-order-list__variant--detail-open`. */
  isLayoutExpanded() {
    const desktop = this.isBulkOrderDesktopViewport();
    if (desktop) {
      return !this.classList.contains('bulk-order-list--desktop-columns-reduced');
    }
    return this.isMobileDetailFullyOpen();
  }

  isMobileDetailFullyOpen() {
    return (
      this.variantRows.length > 0 &&
      this.variantRows.every((row) => row.classList.contains('bulk-order-list__variant--detail-open'))
    );
  }

  isMobileDetailFullyClosed() {
    return !this.variantRows.some((row) => row.classList.contains('bulk-order-list__variant--detail-open'));
  }

  setAllMobileDetailsOpen(open) {
    this.variantRows.forEach((row) => {
      row.classList.toggle('bulk-order-list__variant--detail-open', open);
      const toggle = row.querySelector('.bulk-order-row-toggle');
      toggle?.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  isBulkOrderDesktopViewport() {
    return !this.closest('quick-add-drawer') && globalThis.matchMedia('(min-width: 750px)').matches;
  }

  onViewportChange() {
    if (this.isBulkOrderDesktopViewport()) {
      this.setAllMobileDetailsOpen(false);
    }
    this.syncColumnModeUI();
  }

  connectedCallback() {
    globalThis.addEventListener('beforeunload', this.boundBeforeUnload);
    this.boundMql = globalThis.matchMedia('(min-width: 750px)');
    this.boundMql.addEventListener('change', this.boundMqlChange);
  }

  disconnectedCallback() {
    globalThis.removeEventListener('beforeunload', this.boundBeforeUnload);
    this.boundMql?.removeEventListener('change', this.boundMqlChange);
  }

  syncColumnModeUI() {
    const expanded = this.isLayoutExpanded();
    const opt = Number.parseInt(this.dataset.optCount, 10) || 0;
    const mfCols = Number.parseInt(this.dataset.mfColCount, 10) || 0;
    const tools = this.querySelector('.bulk-order-tools-row');
    if (tools) {
      const hideQtyCol = this.requireLogin ? 1 : 0;
      tools.colSpan = expanded ? opt + 6 + mfCols - hideQtyCol : opt + 7 + mfCols - hideQtyCol;
    }
    const desktop = this.isBulkOrderDesktopViewport();
    this.btnExpand?.classList.toggle('is-active', expanded);
    this.btnReduce?.classList.toggle(
      'is-active',
      desktop ? !expanded : this.isMobileDetailFullyClosed()
    );
  }

  formatProductsSelected(totalQuantity) {
    const strings = globalThis.bulkOrderConfig?.strings ?? {};
    if (totalQuantity === 1) {
      return strings.productsSelectedOne ?? '1';
    }
    const template = strings.productsSelectedOther ?? '{{ count }}';
    return template.replaceAll('__COUNT__', String(totalQuantity));
  }

  updateSubmitButton(totalQuantity) {
    if (!this.submitButton) return;
    // The B2B login gate blocks ordering entirely; keep the button disabled whatever is selected.
    if (this.submitButton.dataset.b2bLocked === 'true') {
      this.submitButton.setAttribute('disabled', 'disabled');
      return;
    }
    // Guests may click straight away - the click takes them to the login instead of the cart.
    if (this.submitButton.dataset.b2bLoginUrl) {
      this.submitButton.removeAttribute('disabled');
      return;
    }
    const atcText = this.submitButton.dataset.atcText ?? '';
    const emptyText = this.submitButton.dataset.emptyText ?? '';
    if (totalQuantity > 0) {
      this.submitButton.removeAttribute('disabled');
      if (this.submitLabelEl) this.submitLabelEl.textContent = atcText;
    } else {
      this.submitButton.setAttribute('disabled', 'disabled');
      if (this.submitLabelEl) this.submitLabelEl.textContent = emptyText;
    }
  }

  onExpandColumns() {
    if (this.isBulkOrderDesktopViewport()) {
      this.classList.remove('bulk-order-list--desktop-columns-reduced');
    } else {
      this.setAllMobileDetailsOpen(true);
    }
    this.syncColumnModeUI();
  }

  onReduceColumns() {
    if (this.isBulkOrderDesktopViewport()) {
      this.classList.add('bulk-order-list--desktop-columns-reduced');
    } else {
      this.setAllMobileDetailsOpen(false);
    }
    this.syncColumnModeUI();
  }

  initializeMobileRowToggles() {
    this.tableBody.querySelectorAll('.bulk-order-row-toggle').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.isBulkOrderDesktopViewport()) return;
        const row = btn.closest('tr');
        if (!row) return;
        row.classList.toggle('bulk-order-list__variant--detail-open');
        const open = row.classList.contains('bulk-order-list__variant--detail-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.syncColumnModeUI();
      });
    });
  }

  /** Toolbar sort: title (Farbe) only, toggles asc → desc → asc. */
  onTitleSort() {
    const direction = this.titleSortDirection;
    this.applySort('title', direction);
    this.titleSortDirection = -direction;
  }

  /** Desktop column header sort: toggles asc/desc per column. */
  onColumnSort(key) {
    if (!key) return;

    let direction = 1;
    if (this.activeSort.key === key) {
      direction = -this.activeSort.direction;
    }

    this.applySort(key, direction);
    if (key === 'title') {
      this.titleSortDirection = -direction;
    }
  }

  applySort(key, direction) {
    this.activeSort = { key, direction };
    const rows = [...this.variantRows];
    rows.sort((a, b) => this.compareSortValues(this.getSortValue(a, key), this.getSortValue(b, key)) * direction);
    rows.forEach((row) => this.tableBody.appendChild(row));
    this.updateSortUI();
  }

  compareSortValues(a, b) {
    const aEmpty = a === '' || a == null;
    const bEmpty = b === '' || b == null;
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    }

    return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
  }

  getSortValue(row, key) {
    const v = this.getVariantData(row);

    switch (key) {
      case 'sku':
        return (v.sku || this.getCellText(row, '.bulk-order-cell--sku') || '').trim();
      case 'title':
        return (v.farbe || row.querySelector('.variant-name')?.textContent?.trim() || v.title || '').trim();
      case 'content':
        return (v.packungsinhalt || this.getCellText(row, '.bulk-order-cell--content') || '').trim();
      case 'size':
        return (v.groesse || this.getCellText(row, '.bulk-order-cell--size') || '').trim();
      case 'option1':
      case 'option2':
      case 'option3':
        return (v[key] || '').trim();
      case 'inventory': {
        const rank = BULK_ORDER_INVENTORY_RANK[v.inventoryLevel] ?? 2;
        return rank * 100000 + (v.inventory_quantity ?? 0);
      }
      case 'price':
        return v.price ?? 0;
      case 'qty':
        return Number.parseInt(row.querySelector('[name="quantity"]')?.value, 10) || 0;
      default:
        return '';
    }
  }

  getCellText(row, selector) {
    const text = row.querySelector(selector)?.textContent?.trim() ?? '';
    return text === '—' ? '' : text;
  }

  updateSortUI() {
    const { key, direction } = this.activeSort;
    const ariaSort = direction === 1 ? 'ascending' : 'descending';

    this.colSortBtns.forEach((btn) => {
      const active = btn.dataset.sortKey === key;
      btn.classList.toggle('is-active', active);
      btn.classList.toggle('is-desc', active && direction === -1);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.querySelectorAll('.bulk-order-table__head-row [data-sort-key]').forEach((th) => {
      const thKey = th.dataset.sortKey;
      if (th.matches('th')) {
        th.setAttribute('aria-sort', thKey === key ? ariaSort : 'none');
      }
    });

    this.btnSorts.forEach((btn) => {
      btn.classList.toggle('is-active', key === 'title');
      btn.classList.toggle('is-desc', key === 'title' && direction === -1);
    });
  }

  getVariantData(row) {
    if (!this.variantDataCache.has(row)) {
      this.variantDataCache.set(row, JSON.parse(row.dataset.variant));
    }
    return this.variantDataCache.get(row);
  }

  initializeVariantRows() {
    this.variantRows.forEach((row) => {
      row.addEventListener('input', (event) => {
        if (event.target.name === 'quantity') this.updateRowPrice(row);
      });
      row.addEventListener('change', (event) => {
        if (event.target.name === 'quantity') this.updateRowPrice(row);
      });
    });
  }

  updateRowPrice(row) {
    const variant = this.getVariantData(row);
    const quantityInput = row.querySelector('[name="quantity"]');
    if (!quantityInput) return;
    const quantity = Number.parseInt(quantityInput.value, 10) || 0;
    const subtotal = quantity * variant.price;
    row.dataset.lineSubtotal = String(subtotal);
    this.updateTotal();
    row.classList.toggle('has-selection', quantity > 0);
  }

  updateTotal() {
    let total = 0;
    let totalQuantity = 0;

    this.variantRows.forEach((row) => {
      total += Number.parseFloat(row.dataset.lineSubtotal) || 0;
      const quantity = Number.parseInt(row.querySelector('[name="quantity"]')?.value, 10) || 0;
      totalQuantity += quantity;
    });

    const totalEl = this.querySelector('.total');
    if (totalEl) totalEl.textContent = formatMoney(total, globalThis.bulkOrderConfig.moneyFormat);
    if (this.selectedTextEl) {
      this.selectedTextEl.textContent = this.formatProductsSelected(totalQuantity);
    }
    this.updateSubmitButton(totalQuantity);
  }

  beforeUnload(event) {
    if (this.requireLogin) return;
    if (this.variantRows.every((row) => (Number.parseInt(row.querySelector('[name="quantity"]')?.value, 10) || 0) <= 0)) return;
    event.preventDefault();
    event.returnValue = '';
  }

  clearSearch() {
    this.searchInput.value = '';
    this.filterBySearch({ target: this.searchInput });
  }

  filterBySearch(event) {
    const searchValue = event.target.value.toLowerCase().trim();

    if (searchValue === '') {
      this.clearSearchState();
    } else {
      this.classList.add('bulk-order-list--search-active');
      this.applySearchFilter(searchValue);
      if (this.tableWrapper) this.tableWrapper.scrollTop = 0;
    }
  }

  clearSearchState() {
    this.classList.remove('bulk-order-list--search-active');
    this.btnClearSearch.style.display = 'none';
    this.variantRows.forEach((row) => {
      row.classList.remove('hidden-by-search', 'matched');
    });
  }

  /** Search: SKU, title, options, barcode, Farbe, Größe, Inhalt metafields. */
  variantSearchHaystack(variantRow) {
    const v = this.getVariantData(variantRow);
    const pieces = [
      variantRow.querySelector('.variant-name')?.textContent,
      variantRow.querySelector('.bulk-order-cell--sku')?.textContent,
      variantRow.querySelector('.bulk-order-cell--content')?.textContent,
      variantRow.querySelector('.bulk-order-cell--size')?.textContent,
      v.sku,
      v.title,
      v.farbe,
      v.groesse,
      v.packungsinhalt,
      v.option1,
      v.option2,
      v.option3,
      v.barcode,
    ];

    return pieces
      .map((p) => (p == null ? '' : String(p).trim()))
      .filter((p) => p !== '' && p !== '—')
      .join(' ')
      .toLowerCase();
  }

  applySearchFilter(searchValue) {
    this.btnClearSearch.style.display = 'flex';

    this.variantRows.forEach((row) => {
      const haystack = this.variantSearchHaystack(row);
      const matched = haystack.includes(searchValue);
      row.classList.toggle('hidden-by-search', !matched);
      row.classList.toggle('matched', matched);
    });
  }

  reviewSelected() {
    this.btnReview.classList.toggle('review-active');

    this.variantRows.forEach((row) => {
      row.classList.remove('hidden-by-review');
    });

    if (!this.btnReview.classList.contains('review-active')) return;

    this.variantRows.forEach((variant) => {
      const qty = Number.parseInt(variant.querySelector('input[name="quantity"]')?.value, 10) || 0;
      variant.classList.toggle('hidden-by-review', qty <= 0);
    });
  }

  buildCartFormData() {
    const formData = new FormData();
    let index = 0;
    const vrgForKey = globalThis.DobiVrgCart?.VRG_FOR_KEY || '_vrg_for';

    this.variantRows.forEach((row) => {
      const quantity = Number.parseInt(row.querySelector('[name="quantity"]')?.value || '0', 10) || 0;
      if (quantity <= 0) return;

      const variant = this.getVariantData(row);
      formData.append(`items[${index}][id]`, variant.id);
      formData.append(`items[${index}][quantity]`, quantity);
      index += 1;

      const vrgVariantId = globalThis.DobiVrgCart?.parseVariantId(row.dataset.vrgVariantId);
      if (vrgVariantId && Number(vrgVariantId) !== Number(variant.id)) {
        formData.append(`items[${index}][id]`, vrgVariantId);
        formData.append(`items[${index}][quantity]`, quantity);
        formData.append(`items[${index}][properties][${vrgForKey}]`, String(variant.id));
        index += 1;
      }
    });

    let sections = 'cart-icon-bubble';
    if (this.cart) {
      sections += `,${this.cart.closest('.shopify-section').id.replace('shopify-section-', '')}`;
    }
    formData.append('sections_url', globalThis.location.pathname);
    formData.append('sections', sections);

    return formData;
  }

  resetQuantityInputs() {
    this.variantRows.forEach((row) => {
      row.querySelector('[name="quantity"]').value = 0;
      row.dataset.lineSubtotal = '0';
    });

    this.variantRows.forEach((row) => row.classList.remove('has-selection'));
    this.updateTotal();
  }

  onCartAddSuccess(response) {
    // Mirror product-form.js: only leave the page when the theme is set to go to the cart page.
    // Reset first, otherwise the beforeUnload guard sees the selection and prompts "leave site?".
    if (globalThis.theme?.settings?.afterAtc === 'page') {
      this.resetQuantityInputs();
      globalThis.location.href = globalThis.theme.routes.cart;
      return;
    }

    if (this.cart) {
      const openDrawer = globalThis.theme?.settings?.afterAtc === 'drawer';
      this.cart.renderContents(response, openDrawer);
    }

    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble && response.sections?.['cart-icon-bubble']) {
      cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
    }

    setTimeout(() => this.resetQuantityInputs(), 700);
  }

  handleSubmit(event) {
    event.preventDefault();
    if (!this.submitButton) return;
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.classList.add('is-loading');

    const fetchRequestOpts = {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: this.buildCartFormData(),
    };

    fetch(globalThis.theme.routes.cartAdd, fetchRequestOpts)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          alert(this.extractErrorMessage(response));
          return;
        }
        this.onCartAddSuccess(response);
      })
      .catch((error) => {
        console.error('Bulk order submit failed:', error);
      })
      .finally(() => {
        this.submitButton.classList.remove('is-loading');
        this.submitButton.removeAttribute('aria-disabled');
      });
  }

  extractErrorMessage(data) {
    if (data.errors && typeof data.errors === 'object') {
      return Object.entries(data.errors)
        .map(([, messages]) => messages.join(', '))
        .join('\n');
    }
    return typeof data.description === 'string' ? data.description : data.message;
  }
}

if (!customElements.get('bulk-order-list')) {
  customElements.define('bulk-order-list', BulkOrderList);
}
